import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { label: "Work", to: "/work" },
  { label: "Studio", to: "/studio" },
  { label: "Process", to: "/process" },
  { label: "Journal", to: "/journal" },
  { label: "Contact", to: "/contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled || open ? "bg-bone/95 backdrop-blur-md border-b border-borderWarm" : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 md:h-20 flex items-center justify-between gap-3">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group min-w-0">
            <img
              src="/logo.png?v=3"
              alt="Muse n Aura Designs"
              className="h-12 md:h-14 w-auto shrink-0 transition-transform duration-500 group-hover:rotate-[8deg]"
            />
            <span className="inline-flex flex-col leading-tight min-w-0">
              <span className="font-serif text-xl md:text-2xl tracking-[0.1em] md:tracking-[0.06em] text-ink whitespace-nowrap">
                Muse<span className="text-gold">n</span>Aura
              </span>
              <span className="text-[10px] md:text-[10px] uppercase tracking-[0.24em] md:tracking-[0.28em] text-muteInk mt-0.5 whitespace-nowrap">
                Designs · Estd 2016
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={`text-[13px] uppercase tracking-[0.22em] ${
                  loc.pathname === n.to ? "text-ink" : "text-muteInk hover:text-ink"
                } transition-colors duration-300`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <Link
            to="/contact"
            data-testid="nav-cta-enquire"
            className="hidden md:inline-flex items-center gap-2 border border-ink px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors duration-300"
          >
            Enquire<span className="text-clay">→</span>
          </Link>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            className="md:hidden relative w-10 h-10 flex items-center justify-center shrink-0"
          >
            <span
              className={`absolute h-[1.5px] w-6 bg-ink transition-transform duration-300 ${open ? "rotate-45" : "-translate-y-1.5"}`}
            />
            <span
              className={`absolute h-[1.5px] w-6 bg-ink transition-transform duration-300 ${open ? "-rotate-45" : "translate-y-1.5"}`}
            />
          </button>
        </div>
      </motion.header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-bone md:hidden pt-20 flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex-1 flex flex-col justify-center gap-1 px-5 pb-16">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-borderWarm"
                >
                  <Link
                    to={n.to}
                    data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                    className={`block py-6 font-serif text-4xl tracking-tight transition-colors ${
                      loc.pathname === n.to ? "text-clay italic" : "text-ink"
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-[0.3em] text-muteInk mr-4 align-middle">0{i + 1}</span>
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="mt-10"
              >
                <Link
                  to="/contact"
                  data-testid="nav-mobile-cta"
                  className="inline-flex items-center gap-3 border border-ink px-6 py-3.5 text-[12px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors duration-300"
                >
                  Enquire <span className="text-clay">→</span>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="mt-14 pt-8 border-t border-borderWarm text-[11px] uppercase tracking-[0.24em] text-muteInk space-y-2"
              >
                <p>studio@musenaura.com</p>
                <p>+91 63663 02902</p>
                <p>Bangalore · India</p>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
